
import React, { useState, useMemo, useRef, useEffect } from 'react';

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface LanguageSelectorProps {
  languages: Language[];
  currentLangCode: string;
  onSelect: (code: string) => void;
  fullWidth?: boolean;
  triggerClassName?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
    languages, 
    currentLangCode, 
    onSelect,
    fullWidth = false,
    triggerClassName = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentLang = useMemo(() => 
    languages.find(l => l.code === currentLangCode) || languages.find(l => l.code === 'en') || languages[0],
    [languages, currentLangCode]
  );

  const filteredLanguages = useMemo(() => {
    if (!searchTerm) return languages;
    const term = searchTerm.toLowerCase();
    return languages.filter(l => 
      l.name.toLowerCase().includes(term) || 
      l.code.toLowerCase().includes(term)
    );
  }, [languages, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
        inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={triggerClassName || `flex items-center gap-2 px-3 py-1.5 bg-[#fbfaf6] dark:bg-[#1a2333] border border-stone-200 dark:border-[#1e293b] rounded-full text-xs font-bold text-stone-600 dark:text-stone-300 hover:border-amber-500 hover:text-amber-500 shadow-sm transition-all`}
        title="Change Language"
      >
        <div className="flex items-center gap-2">
            <span>{currentLang.flag}</span>
            <span className={fullWidth ? '' : 'hidden sm:inline'}>{currentLang.name}</span>
        </div>
        <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className={`${fullWidth ? 'left-0 right-0' : 'right-0 w-64'} absolute mt-2 bg-white dark:bg-[#1a2333] border border-stone-200 dark:border-[#1e293b] rounded-xl shadow-xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200`}>
          <div className="p-2 border-b border-stone-100 dark:border-[#1e293b]">
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-[10px]"></i>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search language..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-4 py-2 bg-stone-50 dark:bg-[#0f1521] border border-stone-100 dark:border-[#1e293b] rounded-lg text-xs outline-none focus:ring-1 focus:ring-amber-500 shadow-inner"
              />
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {filteredLanguages.length > 0 ? (
              filteredLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    onSelect(lang.code);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full flex items-center justify-between px-4 py-2 text-left text-xs transition-colors hover:bg-stone-50 dark:hover:bg-stone-800 ${
                    lang.code === currentLangCode ? 'bg-amber-50 dark:bg-amber-900/10 text-amber-600 dark:text-amber-400 font-bold' : 'text-stone-600 dark:text-stone-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </div>
                  {lang.code === currentLangCode && <i className="fa-solid fa-check text-[10px]"></i>}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-stone-400 text-xs italic">
                No languages found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
